import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HISTORY_FIRST_PAGE, HISTORY_PAGE_SIZE } from '@visious/core';
import { fetchHistoryPage } from '../api/history';
import { getPlatform } from '../platform/instance';
import { useSession } from '../platform/session';
import { historyKey } from './useHistoryList';

/**
 * The account's most recent history, as a set of ids — the second half of "have I watched this"
 * (`listing/model.ts`), and the only half that crosses devices.
 *
 * **The first page and no further**, which is the whole design decision here and a deliberate trade.
 * The endpoint pages 40 bare ids at a time with no summary form, so a complete answer for a reader
 * with a long history is dozens of requests fired on arrival at any page that draws a grid — spent
 * to mark rows nobody asked about. One page is one small request, and it covers the case the mark
 * exists for: a video watched on another device, or before this browser's stored positions were
 * cleared. Older videos simply carry no mark, the same way they carry none today.
 *
 * **Keyed under `historyKey`**, so the two invalidators in `useHistoryList` reach it by prefix: the
 * refetch after a removal or a clear corrects this set too, and `markHistoryStale` leaves it stale
 * for the next read after the Watch page records something. Under `AUTH_QUERY_KEY` with them, which
 * matters here for the reason it matters there — a watch history left in the cache after sign-out is
 * the one piece of state whose lingering is a privacy problem rather than a stale render.
 */
export function useWatchedHistory(): ReadonlySet<string> {
  const session = useSession();
  const grantKey = session.grantedAtEpochSec ?? 0;

  const query = useQuery({
    queryKey: [...historyKey(grantKey), 'recent'],
    queryFn: async () => {
      const boot = await getPlatform();
      if (!boot.ok) return EMPTY_IDS;
      // `null` scopes, the same call the History page makes: on a read the instance is the authority
      // (`useHistoryList`). A refusal is an absence of marks, never an error on somebody's feed.
      const result = await fetchHistoryPage(boot.platform, HISTORY_FIRST_PAGE, HISTORY_PAGE_SIZE, null);
      return result.ok ? result.value.ids : EMPTY_IDS;
    },
    retry: false,
    /*
     * Longer than the History page's 30 s: that page is what the reader is looking at while they
     * change it, this is a decoration on somebody else's grid. Watching a video marks the list stale
     * anyway (`markHistoryStale`), so the next grid after a watch re-reads it.
     */
    staleTime: 5 * 60 * 1000,
    enabled: session.ready && session.facts.signedIn,
  });

  return useMemo(() => new Set(query.data ?? EMPTY_IDS), [query.data]);
}

const EMPTY_IDS: readonly string[] = [];
